import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  User, 
  Calendar, 
  DollarSign, 
  CheckCircle, 
  Clock, 
  XCircle,
  Eye,
  Edit,
  Filter,
  Search,
  Plus,
  TrendingUp
} from 'lucide-react';

// Componentes reutilizables
import SearchBox from '../components/common/SearchBox';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';
import ScrollableTable from '../components/common/ScrollableTable';

// Servicios
import invoiceService from '../services/invoiceService';

const InvoiceListPage = () => {
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();

  // Estados
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    client: '',
    dateFrom: '',
    dateTo: ''
  });

  // Cargar facturas
  const loadInvoices = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await invoiceService.getInvoices({
        page,
        limit: itemsPerPage,
        search: searchTerm,
        status: filters.status,
        paymentStatus: filters.paymentStatus,
        client: filters.client,
        startDate: filters.dateFrom,
        endDate: filters.dateTo
      });
      
      setInvoices(response.invoices || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando facturas:', error);
      showNotification('Error al cargar las facturas', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, itemsPerPage, showNotification]);

  // Manejar filtros
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ 
      status: '', 
      paymentStatus: '', 
      client: '', 
      dateFrom: '', 
      dateTo: '' 
    });
    setCurrentPage(1);
  };

  // Navegación de páginas
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadInvoices(newPage);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Manejar cambio de registros por página
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    loadInvoices(1);
  };

  // Marcar factura como pagada
  const markAsPaid = async (invoiceId) => {
    try {
      await invoiceService.markInvoiceAsPaid(invoiceId);
      showNotification('Factura marcada como pagada exitosamente', 'success');
      loadInvoices(currentPage); // Recargar la página actual
    } catch (error) {
      console.error('Error marcando factura como pagada:', error);
      showNotification('Error al marcar la factura como pagada', 'error');
    }
  };

  // Obtener estado de la factura
  const getInvoiceStatus = (status) => {
    const statusConfig = {
      'DRAFT': { label: 'Borrador', icon: FileText, badgeStyle: { backgroundColor: '#F3F4F6', color: '#4B5563', border: '1px solid #D1D5DB' } },
      'SENT': { label: 'Enviada', icon: Clock, badgeStyle: { backgroundColor: '#DBEAFE', color: '#2563EB', border: '1px solid #93C5FD' } },
      'PAID': { label: 'Pagada', icon: CheckCircle, badgeStyle: { backgroundColor: '#D1FAE5', color: '#059669', border: '1px solid #6EE7B7' } },
      'OVERDUE': { label: 'Vencida', icon: XCircle, badgeStyle: { backgroundColor: '#FEE2E2', color: '#DC2626', border: '1px solid #FCA5A5' } },
      'CANCELLED': { label: 'Cancelada', icon: XCircle, badgeStyle: { backgroundColor: '#FEE2E2', color: '#DC2626', border: '1px solid #FCA5A5' } }
    };
    return statusConfig[status] || statusConfig['DRAFT'];
  };

  // Obtener estado de pago
  const getPaymentStatus = (status) => {
    const statusConfig = {
      'PENDING': { label: 'Pendiente', badgeStyle: { backgroundColor: '#FEF3C7', color: '#D97706', border: '1px solid #FCD34D' } },
      'PAID': { label: 'Pagado', badgeStyle: { backgroundColor: '#D1FAE5', color: '#059669', border: '1px solid #6EE7B7' } },
      'PARTIAL': { label: 'Pago Parcial', badgeStyle: { backgroundColor: '#DBEAFE', color: '#2563EB', border: '1px solid #93C5FD' } },
      'OVERDUE': { label: 'Vencido', badgeStyle: { backgroundColor: '#FEE2E2', color: '#DC2626', border: '1px solid #FCA5A5' } },
      'REFUNDED': { label: 'Reembolsado', badgeStyle: { backgroundColor: '#EDE9FE', color: '#7C3AED', border: '1px solid #C4B5FD' } }
    };
    return statusConfig[status] || statusConfig['PENDING'];
  };

  // Obtener método de pago
  const getPaymentMethod = (method) => {
    const methods = {
      'CASH': 'Efectivo',
      'CARD': 'Tarjeta',
      'TRANSFER': 'Transferencia',
      'CHECK': 'Cheque',
      'OTHER': 'Otro'
    };
    return methods[method] || 'No especificado';
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadInvoices(1);
  }, [loadInvoices]);

  // Recargar cuando cambien los filtros o búsqueda
  useEffect(() => {
    if (currentPage === 1) {
      loadInvoices(1);
    } else {
      setCurrentPage(1);
      loadInvoices(1);
    }
  }, [searchTerm, filters]);

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="text-green-600" size={32} />
            Gestión de Facturas
          </h1>
          <p className="text-gray-600 mt-1">Administra todas las facturas generadas automáticamente</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/sales')}
            className="btn btn-outline"
          >
            <TrendingUp size={16} />
            Ver Ventas
          </button>
        </div>
      </div>

      {/* Contenedor de filtros */}
      <div className="card mb-6 filters-container">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Campo de búsqueda */}
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Número de factura, cliente..."
                className="form-input"
              />
            </div>

            {/* Filtro por estado de factura */}
            <div className="w-48">
              <CustomSelect
                label="Estado Factura"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                options={[
                  { value: '', label: 'Todos los estados' },
                  { value: 'DRAFT', label: 'Borrador' },
                  { value: 'SENT', label: 'Enviada' },
                  { value: 'PAID', label: 'Pagada' },
                  { value: 'OVERDUE', label: 'Vencida' },
                  { value: 'CANCELLED', label: 'Cancelada' }
                ]}
                placeholder="Todos los estados"
              />
            </div>

            {/* Filtro por estado de pago */}
            <div className="w-48">
              <CustomSelect
                label="Estado Pago"
                value={filters.paymentStatus}
                onChange={(value) => handleFilterChange('paymentStatus', value)}
                options={[
                  { value: '', label: 'Todos los pagos' },
                  { value: 'PENDING', label: 'Pendiente' },
                  { value: 'PAID', label: 'Pagado' },
                  { value: 'PARTIAL', label: 'Pago Parcial' },
                  { value: 'OVERDUE', label: 'Vencido' },
                  { value: 'REFUNDED', label: 'Reembolsado' }
                ]}
                placeholder="Todos los pagos"
              />
            </div>

            {/* Filtro por fecha desde */}
            <div className="w-48">
              <label className="form-label">Fecha desde</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="form-input"
              />
            </div>

            {/* Filtro por fecha hasta */}
            <div className="w-48">
              <label className="form-label">Fecha hasta</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="form-input"
              />
            </div>

            {/* Botón limpiar */}
            <div className="w-48">
              <label className="form-label">&nbsp;</label>
              <button
                onClick={clearFilters}
                className="btn btn-outline w-full"
              >
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Selector de registros por página */}
      <div className="records-per-page">
        <label htmlFor="itemsPerPage">Registros por página:</label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Lista de facturas */}
      <ScrollableTable>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Cargando facturas...</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">📄</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filters.status || filters.paymentStatus || filters.dateFrom || filters.dateTo ? 'No se encontraron resultados' : 'No hay facturas'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filters.status || filters.paymentStatus || filters.dateFrom || filters.dateTo ? 
                'No hay facturas que coincidan con los filtros aplicados' : 
                'Las facturas se generan automáticamente cuando se confirman las ventas'
              }
            </p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Factura</th>
                <th>Cliente</th>
                <th>Venta</th>
                <th>Total</th>
                <th>Estado Factura</th>
                <th>Estado Pago</th>
                <th>Método Pago</th>
                <th>Fecha Emisión</th>
                <th>Fecha Vencimiento</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const StatusIcon = getInvoiceStatus(invoice.status).icon;
                const statusConfig = getInvoiceStatus(invoice.status);
                const paymentStatusConfig = getPaymentStatus(invoice.paymentStatus);
                
                return (
                  <tr key={invoice._id}>
                    <td>
                      <div className="font-medium text-gray-900">
                        {invoice.invoiceNumber}
                      </div>
                      <div className="text-sm text-gray-500">
                        ID: {invoice._id?.slice(-6)}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-400" />
                        <span className="font-medium">{invoice.client?.name || 'Cliente'}</span>
                      </div>
                      <div className="text-sm text-gray-500">{invoice.client?.email || ''}</div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <TrendingUp size={16} className="text-gray-400" />
                        <span className="font-medium">#{invoice.sale?._id?.slice(-6) || 'N/A'}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {invoice.sale?.status || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 font-medium text-green-600">
                        <DollarSign size={16} />
                        ${invoice.total?.toLocaleString() || '0'}
                      </div>
                      <div className="text-sm text-gray-500">
                        Subtotal: ${invoice.subtotal?.toLocaleString() || '0'}
                      </div>
                    </td>
                    <td>
                      <span 
                        style={{ 
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '4px 12px', borderRadius: '9999px', 
                          fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap',
                          ...statusConfig.badgeStyle 
                        }}
                      >
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td>
                      <span 
                        style={{ 
                          display: 'inline-flex', alignItems: 'center',
                          padding: '4px 12px', borderRadius: '9999px', 
                          fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap',
                          ...paymentStatusConfig.badgeStyle 
                        }}
                      >
                        {paymentStatusConfig.label}
                      </span>
                    </td>
                    <td>
                      <span className="text-sm text-gray-600">
                        {getPaymentMethod(invoice.paymentMethod)}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar size={16} />
                        {new Date(invoice.issueDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar size={16} />
                        {new Date(invoice.dueDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/invoices/${invoice._id}`)}
                          className="btn btn-primary btn-sm"
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/invoices/${invoice._id}?edit=true`)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: 'white' }}
                          title="Editar factura"
                          disabled={invoice.status === 'CANCELLED' || invoice.status === 'PAID'}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/sales/${invoice.sale?._id}`)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#6366f1', borderColor: '#6366f1', color: 'white' }}
                          title="Ver venta asociada"
                        >
                          <TrendingUp size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </ScrollableTable>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} facturas
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="btn btn-outline btn-sm"
            >
              ← Anterior
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="btn btn-outline btn-sm"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* Sistema de notificaciones */}
      <NotificationSystem 
        notification={notification} 
        onClose={hideNotification} 
      />
    </div>
  );
};

export default InvoiceListPage;
